import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { first, isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { Card } from '@/components/cards/Card'
import { EditColumnsForm } from '@/components/tables/CustomColumns/EditColumnsForm'
import {
  fetchAggregateScoresByConfigId,
  fetchAggregateScoresByMultipleScorecards,
  fetchAggregateScores,
  fetchScorecardByScorecardConfigs,
  fetchStreamingScores,
  revokeStreamedCsvUrl,
} from '@/reducers/scorecards/scorecards.actions'
import { setData, setFilter, setTableHeaders } from '@/reducers/scorecards/scorecards.redux'

import { ViewScoresFilters } from '../../ViewScoresFilters/ViewScoresFilters'
import { ViewScoresOptions } from '../ViewScoresOptions'

// Custom columns on this page is front end only and does not save to the backend database, so it's not using the shared code
export const FiltersSection = () => {
  const dispatch = useDispatch()
  const { organizationid: organizationId } = useSelector((state) => state.currentUser)
  const isBaltoAdmin = organizationId === 1
  const { data, filters, loading } = useSelector((state) => state.scorecards)
  const location = useLocation()
  const isQACopilot = location.pathname.startsWith('/qa-copilot')

  const getAggregateData = () => {
    // Reset the streamed CSV on every Preview click. Only the calls group-by
    // path repopulates it via fetchStreamingScores; without this reset,
    // switching from calls → aggregate would leave the previous streamed
    // export wired up to the Download button. Revoking also frees the Blob,
    // which can be hundreds of MB.
    dispatch(revokeStreamedCsvUrl())
    dispatch(setData('advancedExportsCsvFailed', false))

    if (!isQACopilot) {
      filters.scorecard_type = 'automated'
      if (filters.groupBy.some((item) => item.value === 'calls')) {
        dispatch(fetchScorecardByScorecardConfigs(true, false))
        dispatch(fetchStreamingScores(false))
      } else if (!isEmpty(filters.scorecards) && filters.scorecards.length > 1) {
        dispatch(fetchAggregateScoresByMultipleScorecards('table'))
      } else if (!isEmpty(filters.scorecards)) {
        dispatch(fetchAggregateScoresByConfigId(first(filters.scorecards).value, 'table'))
      } else if (!isBaltoAdmin) {
        dispatch(fetchAggregateScores('table'))
      }
    } else {
      filters.scorecard_type = 'copilot'
      dispatch(fetchScorecardByScorecardConfigs(true, true))
      dispatch(fetchStreamingScores(true))
    }
  }

  useEffect(() => {
    return () => {
      dispatch(setFilter('groupBy', []))
    }
  }, [])

  const setFilteredColumns = (values) => {
    dispatch(setTableHeaders('advancedExports', values || []))
  }

  const isPreviewDisabled = () => {
    if (loading.scores) {
      return true
    }

    if (
      isEmpty(
        data.scorecardConfigs.filter(
          (config) => (filters.callType ? config.call_type === filters.callType : true) // return all configs if no call type selected to fail the conditional
        )
      )
    ) {
      return true
    }

    if (
      !isQACopilot &&
      (isEmpty(filters.metrics) || isEmpty(filters.groupBy) || isEmpty(filters.scorecards))
    ) {
      return true
    }

    if (loading.advancedExportsStreamedCsv) {
      return true
    }

    return false
  }

  return (
    <Card>
      <ViewScoresFilters
        hideFilterButton
        multiScorecard
        hide={{ scorecardConfigs: true, measures: isQACopilot }}
        exports
      />
      {!isQACopilot && <ViewScoresOptions />}
      <div className="form-actions left-aligned mt">
        <Button
          brand
          data-testid="filter-button"
          loading={loading.scores || loading.advancedExportsStreamedCsv}
          disabled={isPreviewDisabled()}
          disabledTooltip={
            isQACopilot
              ? 'At least one scorecard must be selected'
              : 'At least one scorecard, display metric, and grouped by variable must be selected'
          }
          tooltipProps={{ placement: 'top-start' }}
          onClick={getAggregateData}
        >
          Preview
        </Button>
        <ButtonAndFormModal
          buttonLabel="Edit Columns"
          modalTitle="Edit Columns"
          modalId="scorecardExports/editColumns"
          modalProps={{ size: 'tiny' }}
          disabled={
            isEmpty(data?.tableHeaders?.advancedExports) ||
            isEmpty(data?.tableRows?.advancedExports) ||
            isPreviewDisabled()
          }
          form={
            <EditColumnsForm
              allColumns={data?.tableHeaders?.advancedExports}
              handleSubmit={(values) => {
                setFilteredColumns(values)
              }}
            />
          }
        />
      </div>
    </Card>
  )
}
